import React from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { brand } from '../portalTheme';
import { EmptyState, ErrorState, LoadingState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { fetchAllDebits, fetchDebitPayments } from '../../../../services/request/CustomerPortal';
import { formatDate, inr, monthlyPaymentTotals } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalDebitPayments() {
  const { debitId = '' } = useParams();
  const { t, term } = useTranslation();
  const result = useAsync(() => Promise.all([fetchAllDebits(), fetchDebitPayments(debitId)]), [debitId]);
  const [debits = [], payments = []] = result.data ?? [];
  const debit = debits.find(item => item.id === debitId);
  const points = monthlyPaymentTotals(payments);

  return (
    <Box>
      <Button component={RouterLink} to="/customer/debits" startIcon={<ArrowBack />} sx={{ mb: 2 }}>
        {t('portal.debitPayments.back')}
      </Button>
      <PageTitle
        kicker={t('portal.debitPayments.kicker')}
        title={debit ? term('debitType', debit.type, t('portal.common.debit')) : t('portal.debitPayments.fallbackTitle')}
        subtitle={t('portal.debitPayments.subtitle')}
      />
      {result.loading && !result.data ? <LoadingState label={t('portal.debitPayments.loading')} /> : null}
      {result.error ? <ErrorState message={result.error} onRetry={result.reload} /> : null}

      {result.data ? (
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('portal.debitPayments.chartTitle')}
            </Typography>
            {points.length === 0 ? (
              <Typography color="text.secondary">{t('portal.debitPayments.noChart')}</Typography>
            ) : (
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid vertical={false} stroke={brand.border} />
                    <XAxis dataKey="label" tick={{ fill: brand.slate, fontSize: 12 }} />
                    <YAxis tick={{ fill: brand.slate, fontSize: 12 }} tickFormatter={value => inr(Number(value))} width={90} />
                    <Tooltip formatter={value => [inr(Number(value)), t('portal.debitPayments.tooltip')]} cursor={{ fill: brand.goldSoft }} />
                    <Bar dataKey="total" fill={brand.gold} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          {payments.length === 0 ? (
            <EmptyState title={t('portal.debitPayments.empty')} />
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('portal.common.columns.date')}</TableCell>
                    <TableCell>{t('portal.common.columns.type')}</TableCell>
                    <TableCell align="right">{t('portal.common.columns.amount')}</TableCell>
                    <TableCell>{t('portal.common.columns.status')}</TableCell>
                    <TableCell>{t('portal.common.columns.note')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map(payment => (
                    <TableRow key={payment.id} hover>
                      <TableCell>{formatDate(payment.createdDate) ?? '—'}</TableCell>
                      <TableCell>{term('paymentType', payment.paymentType, t('portal.common.payment'))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {inr(payment.amount)}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={payment.status ?? 'RECORDED'} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {payment.note || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : null}
    </Box>
  );
}
